/* eslint-disable react/prop-types */
import React, { useContext, useMemo, useState } from "react";
import NavHeader from "./NavHeader";
import { BsCalendarWeek, BsBookmarkCheckFill } from "react-icons/bs";
import { RiLogoutCircleRLine } from "react-icons/ri";
import { FaUserClock, FaUsers } from "react-icons/fa";
import { IoChatboxEllipsesOutline } from "react-icons/io5";
import { RiUserSettingsLine } from "react-icons/ri";
import { AiOutlineNotification } from "react-icons/ai";
import { MdSwitchAccount, MdWorkHistory } from "react-icons/md";
import { IoIosHelpCircleOutline } from "react-icons/io";
import useLogout from "./useLogout";
import "../../sass/zeplin/ClinicHeader.scss";
import GenericModal from "./GenericModal";
import { ICONS } from "./constants/swal.constants";
import { UserContext } from "components/context/UserContext";
import { IoReturnUpBackOutline } from "react-icons/io5";
import { useHistory, useLocation } from "react-router-dom";
import { restoreDelegateUser } from "services/companyServices";
import {
  Calendar,
  CalendarClock,
  CalendarCheck,
  Users,
  User,
  History,
  MessageSquare,
  HelpCircle,
  LogOut,
  CornerDownLeft,
} from "lucide-react";
import NewNavLink from "./NewNavLink";

export const CalendarIcon = ({ stroke }) => {
  return <BsCalendarWeek fill={stroke} size={35} />;
};

export const AppointmentsIcon = ({ stroke }) => {
  return <FaUserClock fill={stroke} size={35} />;
};

export const BookedIcon = ({ stroke }) => {
  return <BsBookmarkCheckFill fill={stroke} size={35} />;
};

export const AccountsIcon = ({ stroke }) => {
  return <MdSwitchAccount fill={stroke} size={40} />;
};

export const RepsIcon = ({ stroke }) => {
  return <FaUsers fill={stroke} size={40} />;
};

export const ProfileIcon = ({ stroke }) => {
  return <RiUserSettingsLine fill={stroke} size={35} />;
};

export const HistoryIcon = ({ stroke }) => {
  return <MdWorkHistory fill={stroke} size={35} />;
};

export const AnnouncementsIcon = ({ stroke }) => {
  return <AiOutlineNotification fill={stroke} size={35} />;
};

export const LogoutIcon = () => {
  return <RiLogoutCircleRLine fill="#224984" size={35} />;
};
export const ReturnIcon = () => {
  return <IoReturnUpBackOutline fill="#224984" size={35} />;
};
export const ChatIcon = () => {
  return <IoChatboxEllipsesOutline fill="#224984" size={35} />;
};
export const HelpCenterIcon = () => {
  return <IoIosHelpCircleOutline fill="#224984" size={35} />;
};

const links = [
  {
    pathname: "/client/calendar",
    Img: CalendarIcon,
    name: "Calendar",
    descriptor: "Create Appointments",
    icon: Calendar,
  },
  {
    pathname: "/client/template",
    Img: CalendarIcon,
    name: "Recurring",
    descriptor: "Appointment Templates",
    icon: CalendarClock,
  },
  {
    pathname: "/appointments",
    Img: AppointmentsIcon,
    name: "Appointments",
    descriptor: "View Details",
    icon: CalendarCheck,
  },
  {
    pathname: "/client/reps",
    Img: RepsIcon,
    name: "My Reps",
    descriptor: "Manage Representatives",
    icon: Users,
  },
  {
    pathname: "/profile",
    Img: ProfileIcon,
    name: "Profile",
    descriptor: "Your Settings",
    icon: User,
  },
  {
    pathname: "/client/user-activity",
    Img: HistoryIcon,
    name: "Activity",
    descriptor: "User Activity Log",
    icon: History,
  },
  {
    pathname: "/chat",
    Img: ChatIcon,
    name: "Chat",
    descriptor: "Messages",
    icon: MessageSquare,
  },
  {
    pathname: "/help-center",
    Img: HelpCenterIcon,
    name: "Help Center",
    descriptor: "Guides & FAQs",
    icon: HelpCircle,
  },
];

const delegateUserLinks = [
  {
    pathname: "/delegate/calendar",
    Img: CalendarIcon,
    name: "Calendar",
    descriptor: "Create Appointments",
    icon: Calendar,
  },
];

const delegateUserRoutes = ["/delegate/calendar", "/impersonation-options"];

const ClinicHeader = () => {
  const history = useHistory();
  const location = useLocation();
  const { state, dispatch } = useContext(UserContext);

  const [genericModalData, setGenericModalData] = useState({
    show: false,
  });
  const showLogoutSuccess = () => {
    setGenericModalData({
      show: true,
      title: "Goodbye",
      subtext: "See you next time!",
      icon: ICONS.checkMark,
    });
  };
  const showLogoutError = () => {
    setGenericModalData({
      show: true,
      title: "Oops!",
      subtext: "Some error occurred",
      icon: ICONS.error,
    });
  };
  const hideModal = () => {
    setGenericModalData((prevState) => ({ ...prevState, show: false }));
  };
  const handleReturnFromImpersonation = async () => {
    try {
      const res = await restoreDelegateUser();
      if (!res.isSuccessful) {
        history.push("/login");
        return;
      }
      dispatch({
        type: "SET_IMPERSONATING",
        payload: { isImpersonating: false },
      });
      history.push("/impersonation-options");
    } catch (e) {
      console.log({ e });
      history.push("/login");
    }
  };

  const linksToUse = useMemo(() => {
    if (
      delegateUserRoutes.includes(location.pathname) &&
      state?.currentUser?.roleId === 8
    ) {
      return delegateUserLinks;
    }
    return links;
  }, [location.pathname, state?.currentUser?.roleId]);

  const logOut = useLogout(
    "/login",
    showLogoutSuccess,
    showLogoutError,
    hideModal,
  );

  return (
    <>
      {genericModalData.show && (
        <GenericModal
          show={genericModalData.show}
          onHide={hideModal}
          title={genericModalData?.title || ""}
          subtext={genericModalData?.subtext || ""}
          icon={genericModalData?.icon || undefined}
          hideBtn
        />
      )}
      <NavHeader
        links={linksToUse}
        renderLinks={(fromSidebar) => {
          return (
            <>
              {state?.currentUser?.roleId === 8 ? (
                <NewNavLink
                  onClick={handleReturnFromImpersonation}
                  fromSidebar={fromSidebar}
                  item={{
                    pathname: "/impersonation-options",
                    name: "Return",
                    descriptor: "Back to Delegate Options",
                    icon: CornerDownLeft,
                  }}
                />
              ) : (
                <NewNavLink
                  onClick={() => {
                    return logOut();
                  }}
                  fromSidebar={fromSidebar}
                  item={{
                    pathname: "/login",
                    name: "Logout",
                    descriptor: "Sign Out",
                    icon: LogOut,
                  }}
                />
              )}
            </>
          );
        }}
      />
    </>
  );
};

export default ClinicHeader;
